import type { TableV2ColumnMeta } from "../types";

export type PivotAggregation =
  | "sum"
  | "avg"
  | "count"
  | "distinctCount"
  | "min"
  | "max"
  | "median";

export type PivotDateGrouping = "year" | "quarter" | "month" | "week" | "day";

export type PivotShowAs =
  | "value"
  | "pctOfRow"
  | "pctOfColumn"
  | "pctOfTotal"
  | "runningTotal"
  | "rank"
  | "diffFromPrevious"
  | "pctChangeFromPrevious"
  | "yearToDate"
  | "vsSamePeriodLastYear";

export interface PivotField<TData> {
  id: string;
  label: string;
  /** Formatting for values; `date` fields can be grouped by year, quarter, month, week or day */
  type?: "text" | "number" | "currency" | "percent" | "date";
  accessor?: (row: TData) => unknown;
  meta?: TableV2ColumnMeta;
}

export interface PivotDimension {
  field: string;
  dateGrouping?: PivotDateGrouping;
}

export type PivotFormatRule =
  | { type: "colorScale" }
  | { type: "dataBar" }
  | { type: "signColors" }
  | { type: "arrows" }
  | {
      type: "threshold";
      operator: ">" | "<" | ">=" | "<=";
      value: number;
      tone: "success" | "warning" | "danger";
    };

export interface PivotValue {
  id: string;
  field: string;
  aggregation: PivotAggregation;
  showAs?: PivotShowAs;
  label?: string;
  format?: PivotFormatRule[];
}

export interface PivotTopN {
  field: string;
  n: number;
  valueId: string;
  bottom?: boolean;
  /** Groups the rest into an expandable "Other" row */
  showOther?: boolean;
}

export type PivotSort =
  | { by: "label"; desc?: boolean }
  | { by: "value"; valueId: string; columnKey?: string; desc?: boolean };

export interface PivotConfig {
  rows: PivotDimension[];
  columns: PivotDimension[];
  values: PivotValue[];
  rowSubtotals?: boolean;
  columnSubtotals?: boolean;
  rowGrandTotal?: boolean;
  columnGrandTotal?: boolean;
  topN?: PivotTopN[];
  sort?: PivotSort;
}

export type PivotCellValues = Record<string, number | null>;

export interface PivotRowNode {
  key: string;
  path: string[];
  labels: string[];
  label: string;
  field: string;
  depth: number;
  cells: Record<string, PivotCellValues>;
  hasChildren: boolean;
  children?: PivotRowNode[];
  isOther?: boolean;
  recordCount: number;
}

export interface PivotColumnKey {
  key: string;
  path: string[];
  labels: string[];
  kind: "leaf" | "subtotal" | "total";
}

export interface PivotResult {
  rows: PivotRowNode[];
  columnKeys: PivotColumnKey[];
  grandTotal: Record<string, PivotCellValues>;
  recordCount: number;
}

export const KEY_SEPARATOR = "\u001f";
// Sentinels start with the separator, which escaped data keys never contain, so real values can't collide with them
export const TOTAL_KEY = `${KEY_SEPARATOR}total`;
export const OTHER_KEY = `${KEY_SEPARATOR}other`;
export const BLANK_KEY = `${KEY_SEPARATOR}blank`;
